/**
 * QuizCelebration Component
 *
 * Brief transition screen shown between the last quiz question and results.
 * Displays a "Unlocking your insights..." message with a progress indicator.
 */

import React, { useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress, Fade } from '@mui/material';

interface QuizCelebrationProps {
  /** Whether the celebration is visible */
  visible: boolean;
  /** Called when the celebration animation completes */
  onComplete: () => void;
  /** Minimum display time in milliseconds (default: 1500) */
  minDisplayTime?: number;
  /** Whether data fetching is still in progress */
  isDataLoading?: boolean;
}

const QuizCelebration: React.FC<QuizCelebrationProps> = ({
  visible,
  onComplete,
  minDisplayTime = 1500,
  isDataLoading = false,
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (visible) {
      // Record start time
      startTimeRef.current = Date.now();

      // Set minimum display timer
      timerRef.current = setTimeout(() => {
        // Only complete if data is no longer loading
        if (!isDataLoading) {
          onComplete();
        }
      }, minDisplayTime);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [visible, minDisplayTime, onComplete, isDataLoading]);

  // Handle completion when data finishes loading after minDisplayTime
  useEffect(() => {
    if (visible && !isDataLoading && startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed >= minDisplayTime) {
        onComplete();
      }
    }
  }, [visible, isDataLoading, minDisplayTime, onComplete]);

  if (!visible) return null;

  return (
    <Fade in={visible} timeout={300}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          py: 4,
        }}
      >
        {/* Main text */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Unlocking your insights...
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 4,
            textAlign: 'center',
          }}
        >
          Personalizing your experience
        </Typography>

        {/* Progress indicator */}
        <Box sx={{ width: '60%', maxWidth: 200 }}>
          <LinearProgress
            sx={{
              height: 3,
              borderRadius: 2,
              bgcolor: 'divider',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'text.primary',
              },
            }}
          />
        </Box>
      </Box>
    </Fade>
  );
};

export default QuizCelebration;
